import { useState } from 'react';
import Button from '../../../components/Button';
import { questsService } from '../services/quests-service';
import { userService } from '../../../services/userService';

interface ProcessResult {
    userId: string;
    email: string | null;
    success: boolean;
    error?: string;
}

export const BulkJourneyUpdate = () => {
    const [userIds, setUserIds] = useState<string>('');
    const [dayNumber, setDayNumber] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState<ProcessResult[]>([]);
    const [showResults, setShowResults] = useState(false);

    const handleSubmit = async () => {
        // Validate inputs
        const day = parseInt(dayNumber);
        if (isNaN(day) || day < 1) {
            alert('Please enter a valid day number (1 or greater)');
            return;
        }

        // Parse user IDs (split by newlines, commas, or spaces)
        const userIdList = userIds
            .split(/[\n,\s]+/)
            .map(id => id.trim())
            .filter(id => id.length > 0);

        if (userIdList.length === 0) {
            alert('Please enter at least one user ID');
            return;
        }

        // Confirm action
        const confirmed = window.confirm(
            `Are you sure you want to update ${userIdList.length} user(s) to day ${day}?\n\n` +
            `This will set their journey to day ${day - 1} and mark it as completed.`
        );

        if (!confirmed) return;

        // Process updates
        setIsProcessing(true);
        setShowResults(false);
        const processResults: ProcessResult[] = [];

        for (const userId of userIdList) {
            try {
                // Fetch user data to get email
                const userData = await userService.getUserByUserId(userId);
                const userEmail = userData?.email || null;

                // Update journey
                await questsService.updateUserJourney(userId, day - 1, true);
                
                processResults.push({
                    userId,
                    email: userEmail,
                    success: true,
                });
            } catch (error) {
                console.error(`Error updating user ${userId}:`, error);
                processResults.push({
                    userId,
                    email: null,
                    success: false,
                    error: error instanceof Error ? error.message : 'Unknown error',
                });
            }
        }

        setResults(processResults);
        setShowResults(true);
        setIsProcessing(false);
    };

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;
    
    // Generate comma-separated email list for successful updates
    const successfulEmails = results
        .filter(r => r.success && r.email)
        .map(r => r.email)
        .join(', ');

    const handleCopyEmails = () => {
        navigator.clipboard.writeText(successfulEmails);
        alert('Emails copied to clipboard!');
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mt-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Bulk Journey Update</h2>
            
            <div className="space-y-4">
                {/* User IDs Input */}
                <div>
                    <label htmlFor="userIds" className="block text-sm font-medium text-gray-700 mb-2">
                        User IDs (one per line, or comma/space separated)
                    </label>
                    <textarea
                        id="userIds"
                        value={userIds}
                        onChange={(e) => setUserIds(e.target.value)}
                        placeholder="Enter user IDs here&#10;user123&#10;user456&#10;user789"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        rows={8}
                        disabled={isProcessing}
                    />
                </div>

                {/* Day Number Input */}
                <div>
                    <label htmlFor="dayNumber" className="block text-sm font-medium text-gray-700 mb-2">
                        Target Day Number
                    </label>
                    <input
                        type="number"
                        id="dayNumber"
                        value={dayNumber}
                        onChange={(e) => setDayNumber(e.target.value)}
                        placeholder="e.g., 5"
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isProcessing}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                        This will set users to day {dayNumber ? parseInt(dayNumber) - 1 : 'X'} with completion status = true
                    </p>
                </div>

                {/* Submit Button */}
                <div>
                    <Button
                        onClick={handleSubmit}
                        variant="primary"
                        disabled={isProcessing || !userIds.trim() || !dayNumber}
                    >
                        {isProcessing ? 'Processing...' : 'Update Journey for All Users'}
                    </Button>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <h3 className="text-sm font-semibold text-blue-900 mb-2">How it works:</h3>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                        <li>Enter user IDs (one per line or separated by commas/spaces)</li>
                        <li>Enter the target day number</li>
                        <li>The tool will call <code className="bg-blue-100 px-1 rounded">updateUserJourney(userId, dayNumber - 1, true)</code> for each user</li>
                        <li>This sets the journey to the previous day with completion status = true</li>
                        <li>Results will be displayed below after processing</li>
                    </ul>
                </div>

                {/* Results */}
                {showResults && (
                    <div className="mt-6 border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            Results: {successCount} successful, {failureCount} failed
                        </h3>

                        {/* Comma-separated email list */}
                        {successfulEmails && (
                            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                    <h4 className="text-sm font-semibold text-green-900">
                                        Affected User Emails ({successCount})
                                    </h4>
                                    <Button
                                        onClick={handleCopyEmails}
                                        variant="success"
                                    >
                                        Copy Emails
                                    </Button>
                                </div>
                                <p className="text-sm text-green-800 font-mono break-all">
                                    {successfulEmails}
                                </p>
                            </div>
                        )}
                        
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                            {results.map((result, index) => (
                                <div
                                    key={index}
                                    className={`p-3 rounded-md border ${
                                        result.success
                                            ? 'bg-green-50 border-green-200'
                                            : 'bg-red-50 border-red-200'
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex flex-col min-w-0">
                                            <span className="font-mono text-sm break-all">{result.userId}</span>
                                            {result.email && (
                                                <span className="text-xs text-gray-600 mt-1 break-all">{result.email}</span>
                                            )}
                                        </div>
                                        <span
                                            className={`shrink-0 text-sm font-medium ${
                                                result.success ? 'text-green-700' : 'text-red-700'
                                            }`}
                                        >
                                            {result.success ? '✓ Success' : '✗ Failed'}
                                        </span>
                                    </div>
                                    {result.error && (
                                        <p className="text-sm text-red-600 mt-1 break-words">{result.error}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

